import React from 'react';
import { ConfigGroup, ConfigItem } from '../Mfp/PivotConfigurator/PivotConfigurator.types';
import { VisualizeSummaryPivotValueProps } from 'src/pages/Mfp/MfpGrid/visualize-summary-pivot-ag.types';
import { getCellStateFromCells } from 'src/pivot/PivotCells.utils';
import { GridAsyncState, GridToBackgroundLoadingState } from 'src/state/scope/Scope.types';
import { BackgroundDataLoading } from 'src/components/BackgroundDataLoading/BackgroundDataLoading';
import { Tooltip, Button, CircularProgress } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { isEmpty } from 'lodash';

export interface VisualizeContextMenuProps extends Pick<VisualizeSummaryPivotValueProps, 'hasEditableRevision'> {
  cellState: ReturnType<typeof getCellStateFromCells>;
  gridAsyncState: GridAsyncState;
  availableGroup: ConfigGroup[];
  /** Member ids currently applied as top-level filters, used to render selected state. */
  selectedMemberIds: string[];
  /** Fires with the clicked member's id and level for the parent to toggle selection. */
  onMemberSelect: (memberId: string, level?: string) => void;
  favoritesLoading: boolean;
  handleClickUndo: () => void;
  handleClickRedo: () => void;
  handleClickLayoutManagerOpen: () => void;
  handleClickDownload: () => void;
  freezeCells: () => void;
  lockCells: () => void;
  relaxCells: () => void;
}

/**
 * Conditionally renders the icons above and to the right of the grid
 * Return order is important, as it determines the order the icons are rendered in
 */
export const VisualizeContextMenu = (props: VisualizeContextMenuProps): JSX.Element => {
  const {
    cellState,
    hasEditableRevision,
    relaxCells,
    lockCells,
    freezeCells,
    favoritesLoading,
    handleClickDownload,
    handleClickLayoutManagerOpen,
    handleClickRedo,
    handleClickUndo,
    availableGroup,
    selectedMemberIds,
    onMemberSelect,
    gridAsyncState,
  } = props;

  // Background data-loading indicator, mapped from the grid's async state
  // This is always rendered to the left
  const dataLoadingIndicator = (
    <div key="dimension-list" className="left-item">
      <BackgroundDataLoading viewDataState={GridToBackgroundLoadingState[gridAsyncState]} />
    </div>
  );

  // Flatten each dimension's member tree into a single ordered list of members
  // Rendering is intentionally flat for this phase; grouping comes later
  const flattenMembers = (items: ConfigItem[]): ConfigItem[] =>
    items.flatMap((item) => [item, ...(item.children ? flattenMembers(item.children) : [])]);

  // One ButtonGroup per dimension, each rendered on its own line
  const memberSelector = (
    <div key="member-selector" className="mfp-member-selector">
      {availableGroup.map((group) => {
        const members = flattenMembers(group.children ?? []);
        if (isEmpty(members)) {
          return null;
        }
        // Currently-pressed member ids within this group drive the toggle state
        const groupValue = members.filter((m) => selectedMemberIds.includes(m.id)).map((m) => m.id);
        // `onChange` hands back the full pressed set; diff it to find the toggled member
        // so we can forward its id and level to the parent
        const handleChange = (_event: React.MouseEvent<HTMLElement>, newValue: string) => {
          const toggled = members.find((m) => m.id === newValue);
          if (toggled) {
            onMemberSelect(toggled.id, toggled.level);
          }
        };
        return (
          <ToggleButtonGroup
            key={group.id}
            className="member-selector-group"
            size="small"
            value={groupValue}
            onChange={handleChange}
            exclusive={true}
          >
            {members.map((member) => (
              <ToggleButton
                key={member.id}
                value={member.id}
                data-qa={`member-select-${member.id}`}
                className={'top-align-btn'}
              >
                {member.name}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        );
      })}
    </div>
  );

  const contextItems: React.ReactNode[] = [dataLoadingIndicator, memberSelector];
  if (cellState.isUnlockable && hasEditableRevision) {
    contextItems.push(
      <Tooltip key="unconstrain" title="Unlock selected cell(s)" className="tooltip-for-icon">
        <Button
          data-qa="unlock-btn-unlock"
          className={'top-align-btn'}
          style={{ minWidth: 'unset' }}
          onClick={relaxCells}
        >
          <i className="far fa-unlock" />
        </Button>
      </Tooltip>,
    );
  }
  if (cellState.isUnFreezeable && hasEditableRevision) {
    contextItems.push(
      <Tooltip key="unfreeze" title="Unfreeze selected cell(s)" className="tooltip-for-icon">
        <Button
          data-qa="unfreeze-btn-unfreeze"
          className={'top-align-btn'}
          style={{ minWidth: 'unset' }}
          onClick={relaxCells}
        >
          <i className="far fa-fire-alt" />
        </Button>
      </Tooltip>,
    );
  }

  if (cellState.isRelaxed && hasEditableRevision) {
    contextItems.push(
      <Tooltip key="lock" title="Lock selected cell(s)" className="tooltip-for-icon">
        <Button data-qa="lock-btn-lock" className={'top-align-btn'} style={{ minWidth: 'unset' }} onClick={lockCells}>
          <i className="far fa-lock" />
        </Button>
      </Tooltip>,
    );
    contextItems.push(
      <Tooltip key="freeze" title="Freeze selected cell(s)" className="tooltip-for-icon">
        <Button
          data-qa="freeze-btn-freeze"
          className={'top-align-btn'}
          style={{ minWidth: 'unset' }}
          onClick={freezeCells}
        >
          <i className="far fa-snowflake" />
        </Button>
      </Tooltip>,
    );
  }

  if (hasEditableRevision) {
    contextItems.push(
      <Button
        key="undo"
        data-qa="undo-btn-undo"
        className={'top-align-btn'}
        onClick={handleClickUndo}
        startIcon={<i className="far fa-undo-alt" />}
      >
        Undo
      </Button>,
    );
    contextItems.push(
      <Button
        key="redo"
        data-qa="redo-btn-redo"
        className={'top-align-btn'}
        onClick={handleClickRedo}
        startIcon={<i className="far fa-redo-alt" />}
      >
        Redo
      </Button>,
    );
  }

  contextItems.push(
    <Button
      key="configure"
      data-qa="configure-btn-open"
      className={'top-align-btn'}
      onClick={handleClickLayoutManagerOpen}
      startIcon={<i className="far fa-cog" />}
    >
      Configure
    </Button>,
  );

  contextItems.push(
    <Tooltip key="popup_download" className="tooltip-for-icon" title="Download data as XLSX">
      <span>
        <Button
          data-qa="down-btn-download"
          className="down-btn-download"
          onClick={handleClickDownload}
          disabled={favoritesLoading}
        >
          {favoritesLoading ? <CircularProgress size={20} /> : <i className="far fa-cloud-download" />}
        </Button>
      </span>
    </Tooltip>,
  );

  return <React.Fragment>{contextItems}</React.Fragment>;
};
